import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RECURRENCE_LABELS, todayISO } from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, UserPlus } from "lucide-react";

const WEEKDAYS = [
  { v: 0, l: "L" },
  { v: 1, l: "M" },
  { v: 2, l: "X" },
  { v: 3, l: "J" },
  { v: 4, l: "V" },
  { v: 5, l: "S" },
  { v: 6, l: "D" },
];

const empty = () => ({
  title: "",
  description: "",
  assignee_id: "",
  department_id: "",
  date: todayISO(),
  time: "09:00",
  due_date: todayISO(),
  due_time: "18:00",
  priority: "normal",
  requires_evidence: false,
  requires_approval: false,
  recurrence: "none",
  recurrence_days: [],
});

export function NewTaskDialog({ trigger, open, onOpenChange }) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = open !== undefined ? open : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);

  const qc = useQueryClient();
  const { user } = useAuth();

  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "empleado";

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: () => api.get("/employees").then((r) => r.data),
    enabled: isAdmin,
  });

  const { data: departments = [] } = useQuery({
    queryKey: ["departments"],
    queryFn: () => api.get("/departments").then((r) => r.data),
    enabled: isAdmin,
  });

  const set = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const toggleDay = (day) => {
    setForm((current) => ({
      ...current,
      recurrence_days: current.recurrence_days.includes(day)
        ? current.recurrence_days.filter((x) => x !== day)
        : [...current.recurrence_days, day],
    }));
  };

  const resetAndClose = () => {
    setForm(empty());
    setOpen(false);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("El título es obligatorio");
      return;
    }

    /*
     * ADMINISTRADOR
     * -------------------------
     * Debe seleccionar un funcionario.
     */
    if (isAdmin && !form.assignee_id) {
      toast.error("Selecciona un funcionario");
      return;
    }

    /*
     * Solo permitimos crear desde estos dos roles.
     */
    if (!isAdmin && !isEmployee) {
      toast.error("No tienes permisos para crear tareas");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        ...form,
        title: form.title.trim(),
        description: form.description?.trim() || "",
      };

      /*
       * ADMIN:
       * utiliza el endpoint existente.
       *
       * FUNCIONARIO:
       * utiliza el nuevo endpoint seguro.
       *
       * El backend determina automáticamente
       * el funcionario responsable.
       */
      if (isAdmin) {
        await api.post("/tasks", payload);
        toast.success("Tarea creada y asignada correctamente");
      } else {
        await api.post("/tasks/personal", payload);
        toast.success("Tarea personal creada correctamente");
      }

      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["notifications"] });

      resetAndClose();
    } catch (e) {
      toast.error(
        apiError(
          e.response?.data?.detail ||
          "No se pudo crear la tarea"
        )
      );
    } finally {
      setSaving(false);
    }
  };

  /*
   * Si por alguna razón se intenta mostrar el diálogo
   * a un usuario que no tiene permiso, no renderizamos
   * el formulario.
   */
  if (!isAdmin && !isEmployee) {
    return null;
  }

  const title = isAdmin
    ? "Nueva Tarea"
    : "Nueva tarea personal";

  const description = isAdmin
    ? "Completa los datos para crear y asignar una tarea."
    : "Registra una tarea independiente para hacer seguimiento a tu trabajo.";

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && (
        <DialogTrigger asChild>
          {trigger}
        </DialogTrigger>
      )}

      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isAdmin ? (
              <UserPlus className="h-5 w-5" />
            ) : (
              <Plus className="h-5 w-5" />
            )}

            {title}
          </DialogTitle>

          <DialogDescription>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">

          {/* TÍTULO */}
          <div>
            <Label htmlFor="task-title">
              Título *
            </Label>

            <Input
              id="task-title"
              data-testid="task-title-input"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder={
                isAdmin
                  ? "Ej: Revisar caja"
                  : "Ej: Llamar a cliente"
              }
              className="mt-1"
            />
          </div>

          {/* DESCRIPCIÓN */}
          <div>
            <Label htmlFor="task-description">
              Descripción / Instrucciones
            </Label>

            <Textarea
              id="task-description"
              data-testid="task-desc-input"
              value={form.description}
              onChange={(e) =>
                set("description", e.target.value)
              }
              placeholder={
                isAdmin
                  ? "Detalles de la tarea"
                  : "Describe lo que necesitas realizar"
              }
              className="mt-1"
              rows={3}
            />
          </div>

          {/* SOLO ADMINISTRADOR */}
          {isAdmin && (
            <>
              <div>
                <Label>
                  Funcionario *
                </Label>

                <Select
                  value={form.assignee_id}
                  onValueChange={(value) =>
                    set("assignee_id", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona un funcionario" />
                  </SelectTrigger>

                  <SelectContent>
                    {employees
                      .filter((employee) => employee.active)
                      .map((employee) => (
                        <SelectItem
                          key={employee.id}
                          value={employee.id}
                        >
                          {employee.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>
                  Área
                </Label>

                <Select
                  value={form.department_id || undefined}
                  onValueChange={(value) =>
                    set("department_id", value)
                  }
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecciona un área" />
                  </SelectTrigger>

                  <SelectContent>
                    {departments.map((department) => (
                      <SelectItem
                        key={department.id}
                        value={department.id}
                      >
                        {department.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* FUNCIONARIO */}
          {isEmployee && (
            <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs text-blue-700">
              <strong>Tarea personal</strong>
              <br />
              Esta tarea quedará automáticamente asignada a tu
              usuario y también podrá ser supervisada por el
              administrador.
            </div>
          )}

          {/* FECHAS */}
          <div className="grid grid-cols-2 gap-3">

            <div>
              <Label htmlFor="task-date">
                Fecha
              </Label>

              <Input
                id="task-date"
                type="date"
                value={form.date}
                onChange={(e) =>
                  set("date", e.target.value)
                }
                className="mt-1"
                data-testid="task-date-input"
              />
            </div>

            <div>
              <Label htmlFor="task-time">
                Hora
              </Label>

              <Input
                id="task-time"
                type="time"
                value={form.time}
                onChange={(e) =>
                  set("time", e.target.value)
                }
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="task-due-date">
                Fecha límite
              </Label>

              <Input
                id="task-due-date"
                type="date"
                value={form.due_date}
                onChange={(e) =>
                  set("due_date", e.target.value)
                }
                className="mt-1"
                data-testid="task-duedate-input"
              />
            </div>

            <div>
              <Label htmlFor="task-due-time">
                Hora límite
              </Label>

              <Input
                id="task-due-time"
                type="time"
                value={form.due_time}
                onChange={(e) =>
                  set("due_time", e.target.value)
                }
                className="mt-1"
              />
            </div>

          </div>

          {/* PRIORIDAD */}
          <div>
            <Label>
              Prioridad
            </Label>

            <Select
              value={form.priority}
              onValueChange={(value) =>
                set("priority", value)
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="normal">
                  Normal
                </SelectItem>

                <SelectItem value="alta">
                  Alta
                </SelectItem>

                <SelectItem value="urgente">
                  Urgente
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* RECURRENCIA */}
          <div>
            <Label>
              Repetición
            </Label>

            <Select
              value={form.recurrence}
              onValueChange={(value) =>
                set("recurrence", value)
              }
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                {Object.entries(RECURRENCE_LABELS).map(
                  ([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>

            {form.recurrence === "custom" && (
              <div className="mt-2 flex gap-1.5">
                {WEEKDAYS.map((day) => (
                  <button
                    key={day.v}
                    type="button"
                    onClick={() => toggleDay(day.v)}
                    className={`h-9 w-9 rounded-full text-sm font-semibold transition ${
                      form.recurrence_days.includes(day.v)
                        ? "bg-slate-800 text-white"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {day.l}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* EVIDENCIA */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label>
                Requiere evidencia
              </Label>

              <p className="text-xs text-slate-400">
                El responsable deberá adjuntar evidencia.
              </p>
            </div>

            <Switch
              id="ev"
              checked={form.requires_evidence}
              onCheckedChange={(value) =>
                set("requires_evidence", value)
              }
              data-testid="task-evidence-switch"
            />
          </div>

          {/* APROBACIÓN */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
            <div>
              <Label>
                Requiere aprobación
              </Label>

              <p className="text-xs text-slate-400">
                El administrador deberá revisar la tarea.
              </p>
            </div>

            <Switch
              id="ap"
              checked={form.requires_approval}
              onCheckedChange={(value) =>
                set("requires_approval", value)
              }
              data-testid="task-approval-switch"
            />
          </div>

          {/* BOTÓN */}
          <Button
            onClick={submit}
            disabled={saving}
            className="w-full rounded-xl bg-slate-900 hover:bg-slate-800"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Plus className="mr-2 h-4 w-4" />
                {isAdmin
                  ? "Crear Tarea"
                  : "Crear tarea personal"}
              </>
            )}
          </Button>

        </div>
      </DialogContent>
    </Dialog>
  );
}
